import { ThemeOptions } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { cloneDeep } from 'core/common/utils/cloneDeep';
import { mergeDeep } from 'core/common/utils/mergeDeep';
import { paletteDark, paletteLight } from '../colors';
import { contentMaxWidthPx } from '../constants';
import { ThemeBreakpoint, ThemeConfig, ThemeDirection, ThemeMode } from '../entities';
import {
  getButtonOptions,
  getCheckboxOptions,
  getTypographyOptions,
  getProgressBarOptions,
  getFormControlLabelOptions,
  getListOptions,
  getSelectOptions,
  getPaperOptions,
  getModalOptions,
  getDrawerOptions,
  getChipOptions,
  getTextFieldOptions,
  getRatingOptions,
} from '../ui';

const spacingUnitPx = 4;
const mobileBreakpointPx = 1024;

export const getTheme = (
  mode: ThemeMode,
  direction: ThemeDirection,
  themeConfig: ThemeConfig | null = null,
) => {
  const palette = mode === ThemeMode.LIGHT ? paletteLight : paletteDark;
  // palettes are module singletons and mergeDeep mutates its first argument
  const mergedPalette = mergeDeep(cloneDeep(palette), themeConfig?.palette ?? {});

  const baseTheme = createTheme({
    palette: { ...mergedPalette, mode },
    direction,
    spacing: spacingUnitPx,
    breakpoints: {
      values: {
        [ThemeBreakpoint.MOBILE]: mobileBreakpointPx,
        [ThemeBreakpoint.CONTAINER]: contentMaxWidthPx,
      },
    },
  } as unknown as ThemeOptions);
  const { typography, typographyComponents } = getTypographyOptions(baseTheme);
  const { progressBarComponent } = getProgressBarOptions(baseTheme);
  const { buttonComponents } = getButtonOptions(baseTheme);
  const { listComponents } = getListOptions(baseTheme);
  const { selectComponents } = getSelectOptions(baseTheme);
  const { formControlLabelComponents } = getFormControlLabelOptions(baseTheme);
  const { checkboxComponents } = getCheckboxOptions(baseTheme);
  const { paperComponents } = getPaperOptions(baseTheme);
  const { modalComponent } = getModalOptions(baseTheme);
  const { drawerComponent } = getDrawerOptions(baseTheme);
  const { chipComponent } = getChipOptions(baseTheme);
  const { textFieldComponents } = getTextFieldOptions(baseTheme);
  const { ratingComponents } = getRatingOptions();

  return createTheme(baseTheme, {
    typography,
    components: {
      MuiButton: buttonComponents,
      MuiList: listComponents,
      MuiCheckbox: checkboxComponents,
      MuiTypography: typographyComponents,
      MuiLinearProgress: progressBarComponent,
      MuiFormControlLabel: formControlLabelComponents,
      MuiSelect: selectComponents,
      MuiPaper: paperComponents,
      MuiDialog: modalComponent,
      MuiDrawer: drawerComponent,
      MuiChip: chipComponent,
      MuiTextField: textFieldComponents,
      MuiRating: ratingComponents,
    },
  });
};
